// Coverage contracts (COV-*, NED-012).
//
// Coverage crosses the wire as a COUNT OF PEOPLE and nothing else. There is no
// percentage here, no duration, no per-hour segment and no fraction — a need is not
// 73% covered; either two people said yes or one did (COV-006). The absence is the
// design, and `contracts.test.ts` asserts it by name so it cannot be added back by
// somebody reaching for a progress bar.
//
// Nor is there a `covered` boolean anywhere. The kernel's `DerivedCoverage` carries a
// module-private brand so it can only be produced by `deriveCoverage`; these are the
// plain projections of it that a browser can actually receive (COV-001, COV-002,
// invariant 1).

import { z } from "zod";

import type { CommonRejection, Result } from "../errors.js";
import { circleIdSchema, supportNeedIdSchema } from "../primitives.js";

export const needCoverageInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
});

/** A week begins on a local DATE, not an instant — an instant does not know the day. */
export const weekCoverageInput = z.object({
  circleId: circleIdSchema,
  weekStartLocal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "A week starts on a YYYY-MM-DD date."),
});

export const attentionInput = z.object({
  circleId: circleIdSchema,
  limit: z.number().int().min(1).max(100).optional(),
});

export type NeedCoverageInput = z.input<typeof needCoverageInput>;
export type WeekCoverageInput = z.input<typeof weekCoverageInput>;
export type AttentionInput = z.input<typeof attentionInput>;

/**
 * The five user-visible states (plan §10.1).
 *
 * Exactly the kernel's `UserVisibleStatus`, in the kernel's spelling. `isPast` is
 * carried separately because it is a display treatment and NOT a sixth status
 * (plan §10.2): a past need that still needs someone is both, and a card has to be
 * able to say so.
 */
export type CoverageStatus = "needs_someone" | "covered" | "please_recheck" | "done" | "cancelled";

/**
 * Somebody a need is waiting on.
 *
 * The id AND the chosen name, together. An earlier shape carried ids alone on the
 * reasoning that "the UI resolves names from the people it already holds" — but it
 * does not hold them: the plan read model returns display names only for people who
 * have already COMMITTED, and everybody in these two lists is by definition somebody
 * who has not. An id the browser cannot resolve is a surface that quietly degrades
 * from "Mary has been asked" to "somebody has been asked", which is the difference
 * between an organiser knowing who to nudge and not.
 *
 * The name is the circle's chosen name, exactly as `PlanNeedRead.responsible` already
 * carries it — never a real identifier (RULE 2). The id travels alongside so a
 * surface can still key, link or filter without parsing a name.
 */
export interface WaitingPerson {
  readonly personId: string;
  readonly displayName: string;
}

/** One need's coverage, as a count. */
export interface NeedCoverage {
  readonly supportNeedId: string;
  readonly eventId?: string;
  readonly label: string;
  /** The HELP window — never the linked event's (NED-002, NED-010). */
  readonly startsAt: number;
  readonly endsAt: number;
  /** "1 of 2 people confirmed": these two numbers, in this order (NED-012, COV-006). */
  readonly requiredCapacity: number;
  readonly confirmedCapacity: number;
  readonly status: CoverageStatus;
  readonly isPast: boolean;
  /**
   * Who has been asked to recheck and has not answered (COV-004, COV-005).
   *
   * Carried rather than discarded because "Please recheck" is only actionable if you
   * can say who — a status with nobody attached is a colour on a chip.
   */
  readonly awaitingRecheck: readonly WaitingPerson[];
  /**
   * Who has been asked and has not answered yet (REQ-003, plan glossary).
   *
   * Carried **beside** `status`, never folded into it: an open ask contributes
   * nothing to coverage, and a need with two people asked
   * still reads "Needs someone". That is the whole point — an organiser has to be
   * able to feel the difference between asked and answered.
   *
   * Empty is the honest answer for an expired ask. Expiry is derived at read time
   * from the request's own `expiresAt` rather than waiting for a sweep, so a lapsed
   * ask stops counting the moment it lapses instead of the moment a job notices.
   */
  readonly awaitingReply: readonly WaitingPerson[];
}

/** One local day of a week view. Empty days are present, deliberately. */
export interface DayCoverage {
  readonly localDate: string;
  readonly needs: readonly NeedCoverage[];
  /** Counts of NEEDS, not of people — what a week strip is actually asking. */
  readonly needingSomeone: number;
  readonly awaitingRecheck: number;
  readonly covered: number;
}

/**
 * The week's own roll-up (COV-002).
 *
 * Counted on the server, for the same reason every other number here is: the browser
 * renders what it is told. A tally the client assembled from days it happened to have
 * loaded is a second answer to "how is the week going", and it goes wrong exactly
 * when it matters — a partially loaded week reading healthier than it is.
 *
 * Counts of NEEDS. `settled` is done plus cancelled together, because both ask
 * nothing of anybody and a glance does not need them separated; the day's own needs
 * carry the distinction for anyone who opens it.
 */
export interface CoverageSummary {
  readonly totalNeeds: number;
  readonly needingSomeone: number;
  readonly awaitingRecheck: number;
  readonly covered: number;
  readonly settled: number;
}

export interface WeekCoverage {
  readonly weekStartLocal: string;
  /** The circle's zone, so the client renders the same days the server bucketed. */
  readonly timeZone: string;
  readonly days: readonly DayCoverage[];
  /** Plan health at a glance, so the organiser reads it rather than assembling it. */
  readonly summary: CoverageSummary;
}

export type NeedCoverageResult = Result<NeedCoverage, CommonRejection>;
export type WeekCoverageResult = Result<WeekCoverage, CommonRejection>;
export type AttentionResult = Result<readonly NeedCoverage[], CommonRejection>;

/**
 * A compile-time reminder, not decoration.
 *
 * If somebody adds a percentage or a segment array to `NeedCoverage`, this stops
 * compiling — the type below enumerates every field the shape is allowed to have, and
 * `Exclude` leaves `never` only while the two agree. Cheaper than a review comment,
 * and it fires in the pull request that adds the field rather than the one that ships
 * a progress bar.
 */
type AllowedCoverageField =
  | "supportNeedId"
  | "eventId"
  | "label"
  | "startsAt"
  | "endsAt"
  | "requiredCapacity"
  | "confirmedCapacity"
  | "status"
  | "isPast"
  | "awaitingRecheck"
  | "awaitingReply";

const _noSegmentMath: Exclude<keyof NeedCoverage, AllowedCoverageField> extends never
  ? true
  : never = true;
void _noSegmentMath;

/**
 * The same guard over the roll-up, and it is the more tempting one to breach.
 *
 * "3 of 7 needs covered" is one keystroke away from "43% covered", and a percentage
 * on a plan-health panel is a progress bar for a family's week. Adding a field here
 * stops the build.
 */
type AllowedSummaryField =
  "totalNeeds" | "needingSomeone" | "awaitingRecheck" | "covered" | "settled";

const _noHealthScore: Exclude<keyof CoverageSummary, AllowedSummaryField> extends never
  ? true
  : never = true;
void _noHealthScore;
